import React, { useState } from 'react';
import ReportView from './ReportView';
import QuoteDocument from './QuoteDocument';
import { quoteModel } from '../../services/quoteDoc';
import { CURRENT_USER } from '../../services/currentUser';
import { money, packageTotal } from './reportData';
import './QuoteReview.css';

/**
 * Review & send — the last step before the customer gets their quotes (and report).
 *
 * inspection: present when the quotes come from an inspection (report included). Carries the
 *             customer's email / phone, attic size, photo count, hero photo and previewReport().
 *             Absent for a quote-only send from the Quotes page — then only quotes go out.
 */
const QuoteReview = ({ inspection, packageData, onBack, onSend, onDone, onSaveExit, formats: savedFormats, onFormatsChange }) => {
  const hasInspection = !!inspection;
  const packages = Object.values(packageData || {});

  // the customer's contact details are already on the lead — no retyping in front of them
  const [emails, setEmails] = useState([(inspection && inspection.email) || '']);
  const [phones, setPhones] = useState([(inspection && inspection.servicePhone) || '']);
  // per quote: 'summary' (scope only) | 'detailed' (line prices) — kept by the builder when it's there
  const [ownFormats, setOwnFormats] = useState({});
  const formats = savedFormats || ownFormats;
  const setFormats = onFormatsChange || setOwnFormats;
  const [openQuote, setOpenQuote] = useState(null);
  const [includeReport, setIncludeReport] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState('');
  const [sentQuoteOnly, setSentQuoteOnly] = useState(null);

  const formatOf = (pkg) => formats[pkg.id] || 'summary';

  const updateList = (list, setList, idx, value) => {
    const next = [...list];
    next[idx] = value;
    setList(next);
    setError('');
  };

  const sendLabel = () => {
    const n = packages.length;
    const quotes = `${n} QUOTE${n !== 1 ? 'S' : ''}`;
    return hasInspection && includeReport ? `SEND 1 REPORT + ${quotes}` : `SEND ${quotes}`;
  };

  const handleSend = () => {
    const validEmails = emails.map((e) => e.trim()).filter(Boolean);
    const validPhones = phones.map((p) => p.trim()).filter(Boolean);
    if (!validEmails.length && !validPhones.length) {
      setError('Add at least one email or phone number to send to.');
      return;
    }
    const sendData = { inspection, packages, formats, includeReport: hasInspection && includeReport, emails: validEmails, phones: validPhones };
    if (!hasInspection) setSentQuoteOnly(sendData);
    onSend(sendData);
  };

  // quote-only: no report to show — confirm what went out
  if (sentQuoteOnly) {
    return (
      <div className="quote-review">
        <div className="review-sent">
          <div className="review-sent-icon">✓</div>
          <h2>{packages.length} quote{packages.length !== 1 ? 's' : ''} sent</h2>
          <p>To {[...sentQuoteOnly.emails, ...sentQuoteOnly.phones].join(', ')}</p>
          <div className="review-sent-list">
            {packages.map((pkg) => (
              <div key={pkg.id}>
                <span>{pkg.name}</span>
                <b>{money(packageTotal(pkg))}</b>
              </div>
            ))}
          </div>
          <button className="btn-send" onClick={onDone || onBack}>Done</button>
        </div>
      </div>
    );
  }

  if (previewing && inspection && inspection.previewReport) {
    return <ReportView report={inspection.previewReport({ packages, formats })} onBack={() => setPreviewing(false)} />;
  }

  // View: the quote exactly as the customer gets it, with its Summary / Detailed switch
  const viewing = openQuote != null && packages.find((p) => p.id === openQuote);
  if (viewing) {
    const model = quoteModel({
      pkg: viewing,
      format: formatOf(viewing),
      customer: {
        name: inspection && inspection.customerName,
        address: inspection && inspection.serviceAddress,
        email: emails.find((e) => e.trim()) || '',
        phone: phones.find((p) => p.trim()) || '',
      },
      agent: CURRENT_USER.name,
    });
    return (
      <div className="quote-review">
        <div className="review-header">
          <button className="review-back-btn" onClick={() => setOpenQuote(null)}>← Back</button>
          <div className="review-header-content">
            <div className="review-step-label">CUSTOMER VIEW</div>
            <h2>{viewing.name}</h2>
          </div>
          <div className="quote-format">
            {['summary', 'detailed'].map((f) => (
              <button key={f} className={`format-btn ${formatOf(viewing) === f ? 'active' : ''}`} onClick={() => setFormats({ ...formats, [viewing.id]: f })}>
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="review-content quote-view-content">
          <QuoteDocument model={model} />
        </div>
      </div>
    );
  }

  const atticSize = parseFloat(inspection && inspection.atticSize);
  const photoCount = (inspection && inspection.photoCount) || 0;

  return (
    <div className="quote-review">
      {/* HEADER */}
      <div className="review-header">
        <button className="review-back-btn" onClick={onBack}>← Back</button>
        <div className="review-header-content">
          <div className="review-step-label">NEW QUOTE</div>
          <h2>Review & send</h2>
        </div>
        {onSaveExit && (
          <button type="button" className="review-save-exit" onClick={onSaveExit}>
            Save & exit
          </button>
        )}
      </div>

      {/* CONTENT */}
      <div className="review-content">
        {/* CUSTOMER VIEW SECTION */}
        {hasInspection && (
          <div className="review-section">
            <div className="customer-view-header">
              <div className="customer-view-label">CUSTOMER VIEW</div>
              <div className="live-badge">● LIVE</div>
            </div>
            <div className="walk-through-section">
              <h3 className="walk-through-title">Walk-through for</h3>
              <h2 className="customer-name">{inspection.customerName || 'Customer'}</h2>
              <p className="walk-through-desc">Review everything together, confirm the email and phone, then send.</p>
            </div>
          </div>
        )}

        {/* nothing reaches Zoho without the appointment link */}
        {hasInspection && inspection.zohoLinked === false && (
          <div className="review-warning">
            ⚠ Not linked to a Zoho appointment — these quotes won’t be saved in Zoho. Go back to Details and pick the appointment.
          </div>
        )}

        {/* the report's top photo is the front of the home — warn before sending without one */}
        {hasInspection && includeReport && !inspection.heroPhoto && (
          <div className="review-warning">
            ⚠ No front-of-home photo — the top of the report will be blank. Go back to Details to take one.
          </div>
        )}

        {/* INSPECTION SECTION */}
        {hasInspection && (
          <div className="review-section">
            <h4 className="section-title">INSPECTION</h4>
            <div className="inspection-item">
              <div className="inspection-icon">📄</div>
              <div className="inspection-details">
                <div className="inspection-name">Attic Insulation Report</div>
                <div className="inspection-meta">
                  {[atticSize ? `${atticSize.toLocaleString('en-CA')} sqft attic` : '', `${photoCount} photo${photoCount === 1 ? '' : 's'}`]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              <button className="btn-view" onClick={() => setPreviewing(true)}>👁️ View</button>
            </div>
          </div>
        )}

        {/* QUOTES SECTION */}
        {packages.length > 0 && (
          <div className="review-section">
            <h4 className="section-title">QUOTES ({packages.length})</h4>
            <div className="quotes-list">
              {packages.map((pkg, idx) => (
                <div key={pkg.id} className="quote-item">
                  <div className="quote-header">
                    <div className="quote-info">
                      <div className="quote-number">Quote {idx + 1}</div>
                      <div className="quote-name">{pkg.name}</div>
                    </div>
                    <button className="btn-view" onClick={() => setOpenQuote(pkg.id)}>
                      👁️ View
                    </button>
                  </div>
                  <div className="quote-meta">
                    <span className="quote-services">{pkg.services?.length || 0} services</span>
                    <span className="quote-price">{money(packageTotal(pkg))} incl. HST</span>
                  </div>

                  {/* what the customer sees: scope of work only, or each line with its price */}
                  <div className="quote-format">
                    {['summary', 'detailed'].map((f) => (
                      <button
                        key={f}
                        className={`format-btn ${formatOf(pkg) === f ? 'active' : ''}`}
                        onClick={() => setFormats({ ...formats, [pkg.id]: f })}
                      >
                        {f.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SENDING TO SECTION */}
        <div className="review-section">
          <h4 className="section-title">SENDING TO</h4>

          <div className="contact-group">
            <label className="contact-label">EMAIL</label>
            <div className="contact-inputs">
              {emails.map((email, idx) => (
                <div key={idx} className="contact-input-row">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => updateList(emails, setEmails, idx, e.target.value)}
                    placeholder="customer@example.com"
                    className="contact-input"
                  />
                  {emails.length > 1 && (
                    <button className="btn-remove-contact" onClick={() => setEmails(emails.filter((_, i) => i !== idx))} title="Remove email">
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button className="btn-add-contact" onClick={() => setEmails([...emails, ''])}>+ Add email</button>
          </div>

          <div className="contact-group">
            <label className="contact-label">PHONE</label>
            <div className="contact-inputs">
              {phones.map((phone, idx) => (
                <div key={idx} className="contact-input-row">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => updateList(phones, setPhones, idx, e.target.value)}
                    placeholder="+1 (416) 871-7456"
                    className="contact-input"
                  />
                  {phones.length > 1 && (
                    <button className="btn-remove-contact" onClick={() => setPhones(phones.filter((_, i) => i !== idx))} title="Remove phone">
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button className="btn-add-contact" onClick={() => setPhones([...phones, ''])}>+ Add phone</button>
          </div>
        </div>

        {/* ATTACHMENTS SECTION */}
        {hasInspection && (
          <div className="review-section">
            <h4 className="section-title">ATTACHMENTS</h4>
            <label className="attachment-item">
              <input
                type="checkbox"
                className="attachment-checkbox"
                checked={includeReport}
                onChange={(e) => setIncludeReport(e.target.checked)}
              />
              <div className="attachment-details">
                <div className="attachment-name">Inspection report</div>
                <div className="attachment-meta">Sent as a link the customer opens on their phone</div>
              </div>
            </label>
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="review-footer">
        {error && <div className="review-error">{error}</div>}
        <button className="btn-send" onClick={handleSend}>
          ✓ {sendLabel()}
        </button>
      </div>
    </div>
  );
};

export default QuoteReview;
